// react
import { useCallback } from 'react';
import styled from 'styled-components';
import { css } from 'styled-components';

// components
import { Avatar, Button } from '@/components/atoms';

/**
 * OrganizationPreview is a preview of an organization.
 */
export const OrganizationPreview = ({
  imageSrc,
  name,
  onClose,
  toViewMembers,
  onViewMembers,
  toEditOrganization,
  onEditOrganization,
  isActionsDisabled = false,
}: OrganizationPreviewProps) => {
  const labelItem = useCallback(
    (label: string, value: string) => (
      <LabelItem>
        <Label>{label}</Label>
        <Value>{value}</Value>
      </LabelItem>
    ),
    [],
  );
  return (
    <StyledProfilePreview>
      <Avatar src={imageSrc} fallback={'ORG'} size='lg' />
      <InformationContainer>
        <Main>
          <Name>{name}</Name>
        </Main>
        <SectionActionContainer>
          <Section>{name && labelItem('Name', name)}</Section>
          {!isActionsDisabled && (
            <ActionGroup>
              <Button
                ariaLabel='view members'
                variant='primary'
                iconRight={'long-arrow-right'}
                to={toViewMembers}
                onPress={onViewMembers}
              >
                View members
              </Button>
              <Button
                ariaLabel='edit organization'
                variant='primary'
                iconRight={'long-arrow-right'}
                to={toEditOrganization}
                onPress={onEditOrganization}
              >
                Edit organization
              </Button>
              <Button ariaLabel='close' variant='secondary' onPress={onClose}>
                Close
              </Button>
            </ActionGroup>
          )}
        </SectionActionContainer>
      </InformationContainer>
    </StyledProfilePreview>
  );
};

const StyledProfilePreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[20]};

  ${(props) =>
    props.theme.breakpoint.sm(css`
      flex-direction: row;
      gap: ${props.theme.spacing[36]};
    `)}
`;

const InformationContainer = styled.div`
  display: flex;
  flex-direction: column;
  font: ${(props) => props.theme.typography.heading['2xl']};
  color: ${(props) => props.theme.color.neutral[90]};
  gap: ${(props) => props.theme.spacing[24]};
`;

const SectionActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[72]};
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${(props) => props.theme.spacing[20]};
  gap: ${(props) => props.theme.spacing[4]};
`;

const Name = styled.div`
  font: ${(props) => props.theme.typography.heading['5xl']};
  color: ${(props) => props.theme.color.primary[80]};
  white-space: nowrap;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[20]};
`;

const LabelItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[4]};
  border-spacing: 0;
`;

const Label = styled.div`
  font: ${(props) => props.theme.typography.body.md.bold};
  padding-right: ${(props) => props.theme.spacing[32]};
  color: ${(props) => props.theme.color.neutral[90]};
  white-space: nowrap;
`;

const Value = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[80]};
  white-space: nowrap;
`;

const ActionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
`;

interface OrganizationPreviewProps {
  imageSrc?: string;
  name: string;
  onClose?: () => void;
  toViewMembers?: string;
  onViewMembers?: () => void;
  toEditOrganization?: string;
  onEditOrganization?: () => void;
  isActionsDisabled?: boolean;
}

OrganizationPreview.displayName = 'OrganizationPreview';

export default OrganizationPreview;
